import { useEffect, useRef, useState } from "react";
import type {
  AssetDef,
  AudioAssetDef,
  CharacterDef,
  ProjectManifest,
} from "@pix/shared";
import { cacheListByProject } from "../storage/assetStore";

interface Props {
  /** 現在の manifest(parse 済み) */
  manifest: ProjectManifest;
  /** 変更通知。受け側は JSON 化して manifestText に反映する */
  onChange: (next: ProjectManifest) => void;
  /** スクリプトファイル一覧(entry/scenes セレクタ用) */
  scriptFiles: string[];
  /** プレビュー再生で音声 blob を取得するための projectId */
  projectId: string;
}

type SectionKey = "meta" | "characters" | "backgrounds" | "bgm" | "se";

const SECTION_LABEL: Record<SectionKey, string> = {
  meta: "プロジェクト情報",
  characters: "キャラクター",
  backgrounds: "背景",
  bgm: "BGM",
  se: "SE",
};

export function ManifestForm({ manifest, onChange, scriptFiles, projectId }: Props) {
  const [open, setOpen] = useState<Record<SectionKey, boolean>>({
    meta: true,
    characters: true,
    backgrounds: true,
    bgm: false,
    se: false,
  });

  const toggle = (k: SectionKey) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  const patch = (p: Partial<ProjectManifest>) => onChange({ ...manifest, ...p });

  const audioUrls = useAudioBlobs(projectId);

  return (
    <div className="manifest-form">
      <Section
        sectionKey="meta"
        open={open.meta}
        onToggle={() => toggle("meta")}
      >
        <FieldRow label="ID">
          <input
            type="text"
            value={manifest.id}
            onChange={(e) => patch({ id: e.target.value })}
          />
        </FieldRow>
        <FieldRow label="タイトル">
          <input
            type="text"
            value={manifest.title}
            onChange={(e) => patch({ title: e.target.value })}
          />
        </FieldRow>
        <FieldRow label="バージョン">
          <input
            type="text"
            value={manifest.version}
            onChange={(e) => patch({ version: e.target.value })}
          />
        </FieldRow>
        <FieldRow label="エンジン">
          <input
            type="text"
            value={manifest.engineVersion}
            onChange={(e) => patch({ engineVersion: e.target.value })}
          />
        </FieldRow>
        <FieldRow label="entry">
          <select value={manifest.entry} onChange={(e) => patch({ entry: e.target.value })}>
            {scriptFiles.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
            {!scriptFiles.includes(manifest.entry) && (
              <option value={manifest.entry}>{manifest.entry}(未登録)</option>
            )}
          </select>
        </FieldRow>
      </Section>

      <Section
        sectionKey="characters"
        open={open.characters}
        onToggle={() => toggle("characters")}
        count={Object.keys(manifest.characters ?? {}).length}
      >
        <CharacterEditor
          characters={manifest.characters ?? {}}
          onChange={(characters) => patch({ characters })}
        />
      </Section>

      <Section
        sectionKey="backgrounds"
        open={open.backgrounds}
        onToggle={() => toggle("backgrounds")}
        count={Object.keys(manifest.backgrounds ?? {}).length}
      >
        <AssetMapEditor
          assets={manifest.backgrounds ?? {}}
          onChange={(backgrounds) => patch({ backgrounds })}
          fileLabel="画像パス"
          placeholder="bg/street.svg"
        />
      </Section>

      <Section
        sectionKey="bgm"
        open={open.bgm}
        onToggle={() => toggle("bgm")}
        count={Object.keys(manifest.audio?.bgm ?? {}).length}
      >
        <AudioMapEditor
          assets={manifest.audio?.bgm ?? {}}
          onChange={(bgm) =>
            patch({
              audio: {
                bgm,
                se: manifest.audio?.se ?? {},
                ...(manifest.audio?.voice ? { voice: manifest.audio.voice } : {}),
              },
            })
          }
          placeholder="bgm/title.ogg"
          audioUrls={audioUrls}
          showLoop
        />
      </Section>

      <Section
        sectionKey="se"
        open={open.se}
        onToggle={() => toggle("se")}
        count={Object.keys(manifest.audio?.se ?? {}).length}
      >
        <AudioMapEditor
          assets={manifest.audio?.se ?? {}}
          onChange={(se) =>
            patch({
              audio: {
                bgm: manifest.audio?.bgm ?? {},
                se,
                ...(manifest.audio?.voice ? { voice: manifest.audio.voice } : {}),
              },
            })
          }
          placeholder="se/click.ogg"
          audioUrls={audioUrls}
        />
      </Section>
    </div>
  );
}

// ===========================================================
// Section wrapper
// ===========================================================
function Section({
  sectionKey,
  open,
  onToggle,
  count,
  children,
}: {
  sectionKey: SectionKey;
  open: boolean;
  onToggle: () => void;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className={"mf-section" + (open ? " open" : "")}>
      <button className="mf-section-header" onClick={onToggle}>
        <span className="mf-section-arrow">{open ? "▼" : "▶"}</span>
        <span>{SECTION_LABEL[sectionKey]}</span>
        {count !== undefined && <span className="mf-section-count">{count}</span>}
      </button>
      {open && <div className="mf-section-body">{children}</div>}
    </section>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mf-field">
      <span className="mf-field-label">{label}</span>
      <span className="mf-field-control">{children}</span>
    </label>
  );
}

// ===========================================================
// Characters
// ===========================================================
function CharacterEditor({
  characters,
  onChange,
}: {
  characters: Record<string, CharacterDef>;
  onChange: (next: Record<string, CharacterDef>) => void;
}) {
  const updateOne = (id: string, def: CharacterDef) => {
    onChange({ ...characters, [id]: def });
  };
  const renameId = (oldId: string, newId: string) => {
    if (!newId || newId === oldId) return;
    if (characters[newId]) return; // 衝突は無視
    const next: Record<string, CharacterDef> = {};
    for (const [k, v] of Object.entries(characters)) {
      next[k === oldId ? newId : k] = v;
    }
    onChange(next);
  };
  const remove = (id: string) => {
    if (!confirm(`キャラクター「${id}」を削除します。よろしいですか?`)) return;
    const next = { ...characters };
    delete next[id];
    onChange(next);
  };
  const add = () => {
    const base = "new_chara";
    let id = base;
    let n = 1;
    while (characters[id]) id = `${base}_${++n}`;
    onChange({
      ...characters,
      [id]: {
        name: "新規キャラ",
        expressions: ["normal"],
        spritePattern: `chara/${id}_{expression}.svg`,
        facePattern: `face/${id}_{expression}.svg`,
      },
    });
  };

  return (
    <div className="mf-list">
      {Object.entries(characters).map(([id, def]) => (
        <div key={id} className="mf-item mf-item-chara">
          <div className="mf-item-head">
            <input
              type="text"
              className="mf-id"
              defaultValue={id}
              key={id}
              onBlur={(e) => renameId(id, e.target.value.trim())}
              title="識別子(blur で確定)"
            />
            <input
              type="text"
              className="mf-name"
              value={def.name}
              onChange={(e) => updateOne(id, { ...def, name: e.target.value })}
              placeholder="表示名"
            />
            <input
              type="color"
              className="mf-color"
              value={def.defaultColor ?? "#ffd28a"}
              onChange={(e) => updateOne(id, { ...def, defaultColor: e.target.value })}
              title="話者の文字色"
            />
            <button
              className="btn btn-tiny btn-warn"
              onClick={() => remove(id)}
              title="削除"
            >
              ✕
            </button>
          </div>
          <FieldRow label="表情">
            <input
              type="text"
              value={def.expressions.join(", ")}
              onChange={(e) =>
                updateOne(id, {
                  ...def,
                  expressions: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              placeholder="normal, smile, sad, surprise"
            />
          </FieldRow>
          <FieldRow label="立ち絵">
            <input
              type="text"
              value={def.spritePattern ?? ""}
              onChange={(e) => updateOne(id, { ...def, spritePattern: e.target.value })}
              placeholder={`chara/${id}_{expression}.svg`}
            />
          </FieldRow>
          <FieldRow label="顔">
            <input
              type="text"
              value={def.facePattern ?? ""}
              onChange={(e) => updateOne(id, { ...def, facePattern: e.target.value })}
              placeholder={`face/${id}_{expression}.svg`}
            />
          </FieldRow>
        </div>
      ))}
      <button className="btn btn-secondary mf-add" onClick={add}>
        + キャラクターを追加
      </button>
    </div>
  );
}

// ===========================================================
// Generic asset map editor
// ===========================================================
function AssetMapEditor({
  assets,
  onChange,
  fileLabel,
  placeholder,
}: {
  assets: Record<string, AssetDef>;
  onChange: (next: Record<string, AssetDef>) => void;
  fileLabel: string;
  placeholder: string;
}) {
  const update = (id: string, file: string) => {
    onChange({ ...assets, [id]: { file } });
  };
  const renameId = (oldId: string, newId: string) => {
    if (!newId || newId === oldId) return;
    if (assets[newId]) return;
    const next: Record<string, AssetDef> = {};
    for (const [k, v] of Object.entries(assets)) {
      next[k === oldId ? newId : k] = v;
    }
    onChange(next);
  };
  const remove = (id: string) => {
    const next = { ...assets };
    delete next[id];
    onChange(next);
  };
  const add = () => {
    const base = "new";
    let id = base;
    let n = 1;
    while (assets[id]) id = `${base}_${++n}`;
    onChange({ ...assets, [id]: { file: placeholder } });
  };
  return (
    <div className="mf-list">
      {Object.entries(assets).map(([id, def]) => (
        <div key={id} className="mf-item mf-item-asset">
          <input
            type="text"
            className="mf-id"
            defaultValue={id}
            onBlur={(e) => renameId(id, e.target.value.trim())}
            title="ID"
          />
          <input
            type="text"
            className="mf-file"
            value={def.file}
            onChange={(e) => update(id, e.target.value)}
            placeholder={placeholder}
            title={fileLabel}
          />
          <button className="btn btn-tiny btn-warn" onClick={() => remove(id)} title="削除">
            ✕
          </button>
        </div>
      ))}
      <button className="btn btn-secondary mf-add" onClick={add}>
        + 追加
      </button>
    </div>
  );
}

// ===========================================================
// Audio blob URL マップ(プレビュー再生用)
// ===========================================================
function useAudioBlobs(projectId: string): Map<string, string> {
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    let canceled = false;
    const created: string[] = [];
    cacheListByProject(projectId).then((list) => {
      if (canceled) return;
      const m = new Map<string, string>();
      for (const a of list) {
        if (a.mime.startsWith("audio/")) {
          const url = URL.createObjectURL(a.blob);
          m.set(a.path, url);
          created.push(url);
        }
      }
      setUrls(m);
    });
    return () => {
      canceled = true;
      for (const u of created) URL.revokeObjectURL(u);
    };
  }, [projectId]);
  return urls;
}

// ===========================================================
// Audio asset map editor (loopin/loopout 任意 + プレビュー再生)
// ===========================================================
function AudioMapEditor({
  assets,
  onChange,
  placeholder,
  showLoop,
  audioUrls,
}: {
  assets: Record<string, AudioAssetDef>;
  onChange: (next: Record<string, AudioAssetDef>) => void;
  placeholder: string;
  showLoop?: boolean;
  audioUrls: Map<string, string>;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) a.pause();
      audioRef.current = null;
    };
  }, []);

  const stopPreview = () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const togglePreview = (id: string, file: string) => {
    if (playingId === id) {
      stopPreview();
      return;
    }
    const url = audioUrls.get(file);
    if (!url) return;
    stopPreview();
    const a = new Audio(url);
    a.addEventListener("ended", () => {
      if (audioRef.current === a) {
        audioRef.current = null;
        setPlayingId(null);
      }
    });
    audioRef.current = a;
    setPlayingId(id);
    void a.play().catch(() => {
      audioRef.current = null;
      setPlayingId(null);
    });
  };

  const update = (id: string, patch: Partial<AudioAssetDef>) => {
    onChange({ ...assets, [id]: { ...assets[id]!, ...patch } });
  };
  const renameId = (oldId: string, newId: string) => {
    if (!newId || newId === oldId) return;
    if (assets[newId]) return;
    const next: Record<string, AudioAssetDef> = {};
    for (const [k, v] of Object.entries(assets)) {
      next[k === oldId ? newId : k] = v;
    }
    onChange(next);
  };
  const remove = (id: string) => {
    const next = { ...assets };
    delete next[id];
    onChange(next);
  };
  const add = () => {
    const base = "new";
    let id = base;
    let n = 1;
    while (assets[id]) id = `${base}_${++n}`;
    onChange({ ...assets, [id]: { file: placeholder } });
  };
  return (
    <div className="mf-list">
      {Object.entries(assets).map(([id, def]) => {
        const hasBlob = audioUrls.has(def.file);
        const isPlaying = playingId === id;
        return (
        <div key={id} className="mf-item mf-item-audio">
          <div className="mf-item-head">
            <button
              className="mf-audio-preview"
              disabled={!hasBlob}
              onClick={() => togglePreview(id, def.file)}
              title={hasBlob ? "プレビュー再生" : "(音声 blob が未キャッシュ)"}
            >
              {isPlaying ? "■" : "▶"}
            </button>
            <input
              type="text"
              className="mf-id"
              defaultValue={id}
              onBlur={(e) => renameId(id, e.target.value.trim())}
              title="ID"
            />
            <input
              type="text"
              className="mf-file"
              value={def.file}
              onChange={(e) => update(id, { file: e.target.value })}
              placeholder={placeholder}
              title="ファイルパス"
            />
            <button className="btn btn-tiny btn-warn" onClick={() => remove(id)} title="削除">
              ✕
            </button>
          </div>
          {showLoop && (
            <div className="mf-loop-row">
              <label>
                loopin
                <input
                  type="number"
                  value={def.loopin ?? ""}
                  onChange={(e) =>
                    update(id, {
                      loopin: e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                  placeholder="(なし)"
                />
              </label>
              <label>
                loopout
                <input
                  type="number"
                  value={def.loopout ?? ""}
                  onChange={(e) =>
                    update(id, {
                      loopout: e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                  placeholder="(なし)"
                />
              </label>
            </div>
          )}
        </div>
        );
      })}
      <button className="btn btn-secondary mf-add" onClick={add}>
        + 追加
      </button>
    </div>
  );
}
